import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../../../contexts/LanguageContext";
import { hasPermission } from "../../../../utils/permissions";
import { showSuccessToast, showErrorToast, showInfoToast } from "../../../../utils/toastNotifications";
import { ProcessingButtons } from "../../../../components/ProcessingButtons";

interface ValidationErrors {
  [key: string]: string;
}

interface OrderFormProps {
  /** What belongs to the order group is not asked here: payment type, shipping company, delivery mode, route and price per LB. */
  groupOwnsTerms?: boolean;
  formData: any;
  countries: any[];
  currencies: any[];
  paymentMethods: any[];
  categories: any[];
  shippingCompanys: any[];
  cities: any[];
  weightMeasurementUnits: any[];
  lengthMeasurementUnits: any[];
  isLBPriceEnabled: boolean;
  setIsLBPriceEnabled: (enabled: boolean) => void;
  handleInputChange: (event: any) => void;
  handleQuantityChange: (increment: number) => void;
  updateOrder: () => void;
  calculateOrder: () => void;
  resetOrder: () => void;
  handleStatusUpdate: (status: number, comment?: string) => void;
  updating: boolean;
  calculatingOrder: boolean;
  resettingOrder: boolean;
  id: string;
  onValidationChange?: (isValid: boolean) => void;
}

export const OrderForm: React.FC<OrderFormProps> = ({
  groupOwnsTerms = false,
  formData,
  countries,
  currencies,
  paymentMethods,
  categories,
  shippingCompanys,
  cities,
  weightMeasurementUnits,
  lengthMeasurementUnits,
  isLBPriceEnabled,
  setIsLBPriceEnabled,
  handleInputChange,
  handleQuantityChange,
  updateOrder,
  calculateOrder,
  resetOrder,
  handleStatusUpdate,
  updating,
  calculatingOrder,
  resettingOrder,
  id,
  onValidationChange,
}) => {
  const { t } = useTranslation('orders');
  const { isRTL, direction } = useLanguage();

  // Validation state
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  // Partial payment state
  const [virtualCalculationDone, setVirtualCalculationDone] = useState(false);
  const [partialPaymentCalculating, setPartialPaymentCalculating] = useState(false);
  const [virtualCalculating, setVirtualCalculating] = useState(false);
  const [pendingRejectComment, setPendingRejectComment] = useState("");
  // Shipping company capabilities state
  const [hasNormal, setHasNormal] = useState(false);
  const [hasDangerous, setHasDangerous] = useState(false);
  const [hasEconomy, setHasEconomy] = useState(false);
  const [hasFast, setHasFast] = useState(false);

  // Required fields for validation
  const requiredFields = [
    'link', 'merchantName', 'description', 'quantity', 'itemPrice',
    'currencyId', 'destinationCountryId', 'originCountryId', 'categoryId',
    'shippingCompanyId', 'width', 'height', 'length', 'weight', 'paymentType', 'cityId'
  ];

  // Validate all required fields
  const validateAllFields = (): boolean => {
    const newErrors: ValidationErrors = {};
    const newTouched: { [key: string]: boolean } = {};

    requiredFields.forEach(fieldName => {
      newTouched[fieldName] = true;
      const error = validateField(fieldName, formData[fieldName]);
      if (error) {
        newErrors[fieldName] = error;
      }
    });

    // Validate LB price if enabled
    if (isLBPriceEnabled) {
      newTouched['shippingCompanyPricePerLB'] = true;
      const lbError = validateField('shippingCompanyPricePerLB', formData.shippingCompanyPricePerLB);
      if (lbError) {
        newErrors['shippingCompanyPricePerLB'] = lbError;
      }
    }

    setErrors(newErrors);
    setTouched(newTouched);

    const isValid = Object.keys(newErrors).length === 0;
    if (onValidationChange) {
      onValidationChange(isValid);
    }

    return isValid;
  };

  // Handle form submission with validation
  const handleUpdateOrder = () => {
    if (validateAllFields()) {
      updateOrder();
    }
  };

  // Simple validation for required fields only
  const validateField = (name: string, value: any): string => {
    // Check if field is required and empty
    if (requiredFields.includes(name)) {
      // For text fields, check if empty or just whitespace
      if (typeof value === 'string' && !value.trim()) {
        return t('validation.required', 'هذا الحقل مطلوب');
      }

      // For select fields (dropdowns), check if not selected
      if (['currencyId', 'destinationCountryId', 'originCountryId', 'categoryId', 'shippingCompanyId', 'cityId'].includes(name)) {
        if (!value || value === 0) {
          return t('validation.required', 'هذا الحقل مطلوب');
        }
      }

      // Special case for paymentType: 0 is valid (Cash on Delivery)
      if (name === 'paymentType') {
        if (value === null || value === undefined || value === '') {
          return t('validation.required', 'هذا الحقل مطلوب');
        }
      }

      // For numeric fields, check if empty/null/undefined (0 is valid)
      if (['quantity', 'itemPrice', 'width', 'height', 'length', 'weight'].includes(name)) {
        if (value === null || value === undefined || value === '') {
          return t('validation.required', 'هذا الحقل مطلوب');
        }
      }

      // For other fields, just check if empty
      if (value === null || value === undefined || value === '') {
        return t('validation.required', 'هذا الحقل مطلوب');
      }
    }

    // Special case for LB price when enabled
    if (name === 'shippingCompanyPricePerLB' && isLBPriceEnabled) {
      if (value === null || value === undefined || value === '') {
        return t('validation.required', 'هذا الحقل مطلوب');
      }
    }

    return '';
  };

  // Handle input change with validation
  const handleInputChangeWithValidation = (event: any) => {
    const { name, value, type, checked } = event.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    // Call original handler
    handleInputChange(event);

    // Mark field as touched
    setTouched(prev => ({ ...prev, [name]: true }));

    // Validate field
    const error = validateField(name, fieldValue);
    setErrors(prev => ({ ...prev, [name]: error }));

    // Reset virtual calculation state when relevant fields change (for partial payment)
    if (formData.paymentType == 1 && virtualCalculationDone) {
      const fieldsAffectingCalculation = [
        'itemPrice', 'quantity', 'currencyId', 'weight', 'width', 'height', 'length',
        'shippingCompanyId', 'destinationCountryId', 'originCountryId', 'categoryId',
        'merchentDeliveryFee', 'hasShippingInssurance', 'enableDGFees'
      ];

      if (fieldsAffectingCalculation.includes(name)) {
        setVirtualCalculationDone(false);
      }
    }
  };

  // Handle field blur
  const handleFieldBlur = (name: string, value: any) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  // Get field class names
  const getFieldClassName = (fieldName: string) => {
    const baseClass = 'form-control';
    const hasError = touched[fieldName] && errors[fieldName];
    return hasError ? `${baseClass} is-invalid` : baseClass;
  };

  // Render error message
  const renderErrorMessage = (fieldName: string) => {
    if (touched[fieldName] && errors[fieldName]) {
      return (
        <div className="invalid-feedback d-block">
          <small className="text-danger">
            <i className="fa fa-exclamation-circle me-1"></i>
            {errors[fieldName]}
          </small>
        </div>
      );
    }
    return null;
  };



  // Virtual partial payment calculation
  const handleVirtualPartialCalculation = async () => {
    setVirtualCalculating(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_APP_API_BASE_URL}/adminorder/virtual-partial-calculation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (result.success) {
        // Update the form data with the initial partial payment amount
        handleInputChange({
          target: {
            name: 'initialPartialPayment',
            value: result.data.initialPartialPayment,
            type: 'number'
          }
        });
        setVirtualCalculationDone(true);
        // Show toast notification with the partial payment amount
        showInfoToast(`Your initial partial payment is ${result.data.initialPartialPayment}`);
      } else {
        showErrorToast(result.message || 'Virtual calculation failed. Please check your order details and try again.');
      }
    } catch (error) {
      console.error('Error in virtual calculation:', error);
      showErrorToast('Virtual calculation failed. Please check your connection and try again.');
    } finally {
      setVirtualCalculating(false);
    }
  };

  const handleConvertMerchantDeliveryFee = async (baseCurrencyAmount: number, overrideCurrencyId?: string) => {
    const currencyToUse = overrideCurrencyId || formData.currencyId;
    if (baseCurrencyAmount === undefined || baseCurrencyAmount === null || Number.isNaN(baseCurrencyAmount) || !currencyToUse) {
      return;
    }

    if (baseCurrencyAmount === 0) {
      handleInputChange({
        target: {
          name: 'merchentDeliveryFee',
          value: 0,
          type: 'number'
        }
      });
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_APP_API_BASE_URL}/adminorder/convert-merchant-delivery-fee`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: baseCurrencyAmount,
          currencyId: currencyToUse
        })
      });

      const result = await response.json();

      if (result.success) {
        // Update the merchant delivery fee (USD) field
        handleInputChange({
          target: {
            name: 'merchentDeliveryFee',
            value: result.convertedAmount,
            type: 'number'
          }
        });
      } else {
        showErrorToast(result.message || 'Failed to convert merchant delivery fee');
      }
    } catch (error) {
      console.error('Error converting merchant delivery fee:', error);
      showErrorToast('Failed to convert merchant delivery fee. Please check your connection and try again.');
    }
  };

  // Partial payment validation and calculation
  const handlePartialPaymentValidationCalculation = async () => {
    setPartialPaymentCalculating(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_APP_API_BASE_URL}/adminorder/partial-payment-validation-calculation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok) {
        showSuccessToast(result.Message || 'Partial payment validated and calculated successfully');
        // Refresh the order data after a short delay to show the success message
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        showErrorToast(result.message || result.Message || 'Validation and calculation failed. Please check your order details and try again.');
      }
    } catch (error) {
      console.error('Error in partial payment validation:', error);
      showErrorToast('Validation and calculation failed. Please check your connection and try again.');
    } finally {
      setPartialPaymentCalculating(false);
    }
  };

  // Update shipping company capabilities when shippingCompanyId changes
  React.useEffect(() => {
    console.log('useEffect triggered - shippingCompanyId:', formData.shippingCompanyId);
    console.log('shippingCompanys:', shippingCompanys);

    if (formData.shippingCompanyId) {
      const selected = shippingCompanys.find(c => c.id === parseInt(formData.shippingCompanyId));
      console.log('Selected company:', selected);

      if (selected) {
        console.log('Company properties:', {
          HasNormal: selected.HasNormal,
          HasDangerous: selected.HasDangerous,
          HasEconomy: selected.HasEconomy,
          HasFast: selected.HasFast,
          hasNormal: selected.hasNormal,
          hasDangerous: selected.hasDangerous,
          hasEconomy: selected.hasEconomy,
          hasFast: selected.hasFast
        });

        setHasNormal(selected.HasNormal || selected.hasNormal || false);
        setHasDangerous(selected.HasDangerous || selected.hasDangerous || false);
        setHasEconomy(selected.HasEconomy || selected.hasEconomy || false);
        setHasFast(selected.HasFast || selected.hasFast || false);
      } else {
        setHasNormal(false);
        setHasDangerous(false);
        setHasEconomy(false);
        setHasFast(false);
      }
    } else {
      setHasNormal(false);
      setHasDangerous(false);
      setHasEconomy(false);
      setHasFast(false);
    }
  }, [formData.shippingCompanyId, shippingCompanys]);

  // Reset partial payment state when payment type changes
  React.useEffect(() => {
    if (formData.paymentType != 1) {
      setVirtualCalculationDone(false);
      setPartialPaymentCalculating(false);
      setVirtualCalculating(false);
    }
  }, [formData.paymentType]);

  return (
    <div className={`order-form ${isRTL ? 'rtl' : ''}`} dir={direction}>
      {/* Product Information Section */}
      <div className="card mb-4">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">
            <i className="fas fa-box-open me-2"></i>
            {t('sections.productInformation')}
          </h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <label className="form-label">
                {t('fields.link')} <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={getFieldClassName('link')}
                name="link"
                value={formData.link || ""}
                onChange={handleInputChangeWithValidation}
                onBlur={() => handleFieldBlur('link', formData.link)}
                placeholder={t('placeholders.enterLink')}
              />
              {renderErrorMessage('link')}
            </div>
            <div className="col-md-6">
              <label className="form-label">
                {t('fields.merchantName')} <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={getFieldClassName('merchantName')}
                name="merchantName"
                value={formData.merchantName || ""}
                onChange={handleInputChangeWithValidation}
                onBlur={() => handleFieldBlur('merchantName', formData.merchantName)}
                placeholder={t('placeholders.enterMerchantName')}
              />
              {renderErrorMessage('merchantName')}
            </div>
          </div>

          <div className="row mt-3">
            <div className="col-md-6">
              <label className="form-label">
                {t('fields.itemOptions')} <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={getFieldClassName('description')}
                name="description"
                placeholder={t('placeholders.enterDescription')}
                value={formData.description || ""}
                onChange={handleInputChangeWithValidation}
                onBlur={() => handleFieldBlur('description', formData.description)}
              />
              {renderErrorMessage('description')}
            </div>
            <div className="col-md-6">
              <label className="form-label">{t('fields.extendedDescription')}</label>
              <textarea
                className="form-control"
                name="extendedDescription"
                value={formData.extendedDescription || ""}
                onChange={handleInputChange}
                placeholder={t('placeholders.enterExtendedDescription')}
              />
            </div>
          </div>

          <div className="row mt-3">
            <div className="col-md-6">
              <label className="form-label">
                {t('fields.category')} <span className="text-danger">*</span>
              </label>
              <select
                key={formData.categoryId}
                className={getFieldClassName('categoryId')}
                name="categoryId"
                value={formData.categoryId}
                onChange={handleInputChangeWithValidation}
                onBlur={() => handleFieldBlur('categoryId', formData.categoryId)}
              >
                <option value="">{t('placeholders.selectCategory')}</option>
                {categories.map((category: any) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              {renderErrorMessage('categoryId')}
            </div>

          </div>
        </div>
      </div>
      {/* Pricing & Quantity Section */}
      <div className="card mb-4">
        <div className="card-header bg-warning text-dark">
          <h5 className="mb-0">
            {/* <i className="fas fa-calculator me-2"></i> */}
            {t('sections.pricingQuantity')}
          </h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-3">
              <label className="form-label">
                {t('fields.quantity')} <span className="text-danger">*</span>
              </label>
              <div className={`d-flex ${isRTL ? 'flex-row-reverse' : ''}`}>
                <button
                  className={`btn btn-outline-primary btn-sm rounded-circle ${isRTL ? 'ms-1' : 'me-1'}`}
                  onClick={() => handleQuantityChange(-1)}
                >
                  −
                </button>
                <span className="px-2">{formData.quantity}</span>
                <button
                  className={`btn btn-outline-primary btn-sm rounded-circle ${isRTL ? 'me-1' : 'ms-1'}`}
                  onClick={() => handleQuantityChange(1)}
                >
                  +
                </button>
              </div>
              <input
                type="hidden"
                name="quantity"
                value={formData.quantity}
                onChange={handleInputChangeWithValidation}
                onBlur={() => handleFieldBlur('quantity', formData.quantity)}
              />
              {renderErrorMessage('quantity')}
            </div>
            <div className="col-md-3">
              <label className="form-label">
                {t('fields.itemPrice')} <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                className={getFieldClassName('itemPrice')}
                name="itemPrice"
                value={formData.itemPrice}
                onChange={handleInputChangeWithValidation}
                onBlur={() => handleFieldBlur('itemPrice', formData.itemPrice)}
                placeholder={t('placeholders.enterItemPrice')}
              />
              {renderErrorMessage('itemPrice')}
            </div>
            <div className="col-md-3">
              <label className="form-label">{t('fields.merchantDeliveryFee')}</label>
              <input
                type="number"
                className={getFieldClassName('merchentDeliveryFeeInBaseCurrency')}
                name="merchentDeliveryFeeInBaseCurrency"
                value={formData.merchentDeliveryFeeInBaseCurrency}
                onChange={(e) => {
                  handleInputChangeWithValidation(e);
                  // Call conversion on change
                  if (e.target.value !== '') {
                    handleConvertMerchantDeliveryFee(parseFloat(e.target.value));
                  } else {
                    handleConvertMerchantDeliveryFee(0);
                  }
                }}
                placeholder={t('placeholders.enterMerchantDeliveryFee', 'Enter merchant delivery fee')}
              />
              {renderErrorMessage('merchentDeliveryFeeInBaseCurrency')}
            </div>
            <div className="col-md-3">
              <label className="form-label">{t('fields.merchantDeliveryFee')} (USD)</label>
              <input
                type="number"
                className={getFieldClassName('merchentDeliveryFee')}
                name="merchentDeliveryFee"
                value={formData.merchentDeliveryFee}
                onChange={(e) => handleInputChangeWithValidation(e)}
                placeholder="Auto-calculated"
              />
              {renderErrorMessage('merchentDeliveryFee')}
            </div>
            <div className="col-md-3">
              <label className="form-label">
                {t('fields.currency')} <span className="text-danger">*</span>
              </label>
              <select
                className={getFieldClassName('currencyId')}
                name="currencyId"
                value={formData.currencyId || ""}
                onChange={(e) => {
                  handleInputChangeWithValidation(e);
                  const baseFee = formData.merchentDeliveryFeeInBaseCurrency;
                  if (baseFee !== undefined && baseFee !== null && baseFee !== '') {
                    handleConvertMerchantDeliveryFee(parseFloat(baseFee), e.target.value);
                  }
                }}
                onBlur={() => handleFieldBlur('currencyId', formData.currencyId)}
              >
                <option value="">{t('placeholders.selectCurrency')}</option>
                {currencies.map((item: any) => (
                  <option key={item.id} value={item.id}>
                    {item.name + "(" + item.lastExchangeRate + ")"}
                  </option>
                ))}
              </select>
              {renderErrorMessage('currencyId')}
            </div>
          </div>

          <div className="row mt-3">
            <div className="col-md-6">
              <label className="form-label">{t('fields.purchaseMethod', 'Purchase Method')}</label>
              <select
                key={formData.paymentMethodId}
                className="form-control"
                name="paymentMethodId"
                value={formData.paymentMethodId}
                onChange={handleInputChange}
              >
                <option value="">{t('placeholders.selectPurchaseMethod', 'Select Purchase Method')}</option>
                {paymentMethods.map((paymentMethod: any) => (
                  <option key={paymentMethod.id} value={paymentMethod.id}>
                    {paymentMethod.name + "(" + paymentMethod.percent + ")"}
                  </option>
                ))}
              </select>
            </div>
            {/* Inside an order group this belongs to the group and is set there. */}
            {!groupOwnsTerms && (
            <div className="col-md-6">
              <label className="form-label">{t('fields.paymentType')} <span className="text-danger">*</span></label>
              <select
                className={getFieldClassName('paymentType')}
                name="paymentType"
                value={formData.paymentType}
                onChange={handleInputChangeWithValidation}
                onBlur={() => handleFieldBlur('paymentType', formData.paymentType)}
              >
                <option value="">{t('placeholders.selectPaymentType')}</option>
                <option value={0}>{t('paymentTypes.0')}</option>
                <option value={1}>{t('paymentTypes.1')}</option>
                <option value={2}>{t('paymentTypes.2')}</option>
              </select>
              {renderErrorMessage('paymentType')}
            </div>
            )}
          </div>

          {/* Initial Partial Payment Field - Show when Partial Payment is selected (either from backend data or after virtual calculation) */}
          {!groupOwnsTerms && formData.paymentType == 1 && (virtualCalculationDone || formData.initialPartialPayment) && (
            <div className="row mt-3">
              <div className="col-md-6">
                <label className="form-label">
                  {t('fields.initialPartialPayment')} <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className={getFieldClassName('initialPartialPayment')}
                  name="initialPartialPayment"
                  value={formData.initialPartialPayment || ''}
                  onChange={handleInputChangeWithValidation}
                  onBlur={() => handleFieldBlur('initialPartialPayment', formData.initialPartialPayment)}
                  placeholder={t('placeholders.enterInitialPartialPayment')}
                />
                {renderErrorMessage('initialPartialPayment')}
              </div>
            </div>
          )}

          {/* Additional Cost Field */}
          {hasPermission("Add Additional Cost for Order") && (
            <div className="row mt-3">
              <div className="col-md-6">
                <label className="form-label">{t('fields.additionalCost')}</label>
                <input
                  type="number"
                  className="form-control"
                  name="additionaCost"
                  value={formData.additionaCost}
                  onChange={handleInputChange}
                  step={0.01}
                  placeholder={t('placeholders.enterAdditionalCost')}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Dimensions & Weight Section */}
      <div className="card mb-4">
        <div className="card-header bg-info text-white">
          <h5 className="mb-0">
            <i className="fas fa-ruler-combined me-2"></i>
            {t('sections.dimensionsWeight')}
          </h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-2">
              <label className="form-label">
                {t('fields.lengthMeasurementUnit')}
              </label>
              <select
                className="form-select"
                name="lengthMeasurementUnitId"
                value={formData.lengthMeasurementUnitId || ''}
                onChange={handleInputChange}
              >
                <option value="">{t('placeholders.selectLengthUnit')}</option>
                {lengthMeasurementUnits.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">
                {t('fields.unitWidth')} <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                className={getFieldClassName('width')}
                name="width"
                value={formData.width}
                onChange={handleInputChangeWithValidation}
                onBlur={() => handleFieldBlur('width', formData.width)}
                placeholder={t('placeholders.enterWidth')}
              />
              {renderErrorMessage('width')}
            </div>
            <div className="col-md-2">
              <label className="form-label">
                {t('fields.unitHeight')} <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                className={getFieldClassName('height')}
                name="height"
                value={formData.height}
                onChange={handleInputChangeWithValidation}
                onBlur={() => handleFieldBlur('height', formData.height)}
                placeholder={t('placeholders.enterHeight')}
              />
              {renderErrorMessage('height')}
            </div>
            <div className="col-md-2">
              <label className="form-label">
                {t('fields.unitLength')} <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                className={getFieldClassName('length')}
                name="length"
                value={formData.length}
                onChange={handleInputChangeWithValidation}
                onBlur={() => handleFieldBlur('length', formData.length)}
                placeholder={t('placeholders.enterLength')}
              />
              {renderErrorMessage('length')}
            </div>
            <div className="col-md-2">
              <label className="form-label">
                {t('fields.weightMeasurementUnit')}
              </label>
              <select
                className="form-select"
                name="weightMeasurementUnitId"
                value={formData.weightMeasurementUnitId || ''}
                onChange={handleInputChange}
              >
                <option value="">{t('placeholders.selectWeightUnit')}</option>
                {weightMeasurementUnits.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">
                {t('fields.unitWeight')} <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                className={getFieldClassName('weight')}
                name="weight"
                value={formData.weight}
                onChange={handleInputChangeWithValidation}
                onBlur={() => handleFieldBlur('weight', formData.weight)}
                placeholder={t('placeholders.enterWeight')}
              />
              {renderErrorMessage('weight')}
            </div>
            
          </div>
        </div>
      </div>
      {/* Location & Shipping Section */}
      {/* Inside an order group the route belongs to the group and is set there. */}
      {!groupOwnsTerms && (
      <div className="card mb-4">
        <div className="card-header bg-success text-white">
          <h5 className="mb-0">
            <i className="fas fa-shipping-fast me-2"></i>
            {t('sections.locationShipping')}
          </h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <label className="form-label">
                {t('fields.originCountry')} <span className="text-danger">*</span>
              </label>
              <select
                key={formData.originCountryId}
                className={getFieldClassName('originCountryId')}
                name="originCountryId"
                value={formData.originCountryId}
                onChange={handleInputChangeWithValidation}
                onBlur={() => handleFieldBlur('originCountryId', formData.originCountryId)}
              >
                <option value="">{t('placeholders.selectOriginCountry')}</option>
                {countries
                  .filter((x: any) => x.countryType == 2 || x.countryType == 0)
                  .map((country: any) => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
              </select>
              {renderErrorMessage('originCountryId')}
            </div>
            <div className="col-md-6">
              <label className="form-label">
                {t('fields.destinationCountry')} <span className="text-danger">*</span>
              </label>
              <select
                key={formData.destinationCountryId}
                className={getFieldClassName('destinationCountryId')}
                name="destinationCountryId"
                value={formData.destinationCountryId}
                onChange={handleInputChangeWithValidation}
                onBlur={() => handleFieldBlur('destinationCountryId', formData.destinationCountryId)}
              >
                <option value="">{t('placeholders.selectDestinationCountry')}</option>
                {countries
                  .filter((x: any) => x.countryType == 1 || x.countryType == 0)
                  .map((country: any) => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
              </select>
              {renderErrorMessage('destinationCountryId')}
            </div>
          </div>

          <div className="row mt-3">
            <div className="col-md-6">
              <label className="form-label">
                {t('fields.city')} <span className="text-danger">*</span>
              </label>
              <select
                key={formData.cityId}
                className={getFieldClassName('cityId')}
                name="cityId"
                value={formData.cityId || ""}
                onChange={handleInputChangeWithValidation}
                onBlur={() => handleFieldBlur('cityId', formData.cityId)}
              >
                <option value="">{t('placeholders.selectCity')}</option>
                {cities
                  .filter((city: any) => city.countryId == formData.destinationCountryId)
                  .map((city: any) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
              </select>
              {renderErrorMessage('cityId')}
            </div>
          </div>
        </div>
      </div>
      )}



      {/* Shipping Options Section */}
      <div className="card mb-4">
        <div className="card-header bg-secondary text-white">
          <h5 className="mb-0">
            <i className="fas fa-sliders-h me-2"></i>
            {t('sections.shippingOptions')}
          </h5>
        </div>
        <div className="card-body">
          <div className="row">
            {/* Inside an order group this belongs to the group and is set there. */}
            {!groupOwnsTerms && (
            <div className="col-md-6">
              <label className="form-label">
                {t('fields.shippingCompany')} <span className="text-danger">*</span>
              </label>
              <select
                className={getFieldClassName('shippingCompanyId')}
                name="shippingCompanyId"
                value={formData.shippingCompanyId || ""}
                onChange={handleInputChangeWithValidation}
                onBlur={() => handleFieldBlur('shippingCompanyId', formData.shippingCompanyId)}
              >
                <option value="">{t('placeholders.selectShippingCompany')}</option>
                {shippingCompanys.map((item: any) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
              {renderErrorMessage('shippingCompanyId')}
            </div>
            )}
          </div>

          

          <div className="row mt-3">
            <div className="col-md-6">
              <label className="form-label">{t('fields.itemType')}</label>
              <select
                key={`itemType-${hasNormal}-${hasDangerous}`}
                className="form-control"
                name="itemType"
                value={formData.itemType}
                onChange={handleInputChange}
              >
                <option value="">{t('placeholders.selectItemType', 'Select Item Type')}</option>
                {hasNormal && (
                  <option value={0}>
                    {t('itemTypes.0')}
                  </option>
                )}

                {hasDangerous && (
                  <option value={1}>
                    {t('itemTypes.1')}
                  </option>
                )}

              </select>
            </div>
            {/* Inside an order group this belongs to the group and is set there. */}
            {!groupOwnsTerms && (
            <div className="col-md-6">
              <label className="form-label">{t('fields.deliveryMode')}</label>
              <select
                key={`deliveryMode-${hasEconomy}-${hasFast}`}
                className="form-control"
                name="deliveryMode"
                value={formData.deliveryMode}
                onChange={handleInputChange}
              >
                <option value="">{t('placeholders.selectDeliveryMode', 'Select Delivery Mode')}</option>
                {hasEconomy && (
                  <option value={0}>
                    {t('deliveryModes.0')}
                  </option>
                )}

                {hasFast && (
                  <option value={1}>
                    {t('deliveryModes.1')}
                  </option>
                )}

              </select>
            </div>
            )}
          </div>
          <div className="row mt-3">
            {/* Inside an order group the price per LB is agreed for the whole group and set there. */}
            {!groupOwnsTerms && (
            <div className="col-md-4">
              <div className="form-check form-switch d-flex align-items-center mb-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="lbTransportPriceSwitch"
                  checked={isLBPriceEnabled}
                  onChange={() => setIsLBPriceEnabled(!isLBPriceEnabled)}
                  style={{ margin: 0 }}
                />
                <label className="form-check-label mb-0" htmlFor="lbTransportPriceSwitch">
                  {t('fields.lbTransportPrice')}
                </label>
              </div>
              <input
                type="number"
                className={getFieldClassName('shippingCompanyPricePerLB')}
                name="shippingCompanyPricePerLB"
                value={formData.shippingCompanyPricePerLB}
                onChange={handleInputChangeWithValidation}
                onBlur={() => handleFieldBlur('shippingCompanyPricePerLB', formData.shippingCompanyPricePerLB)}
                disabled={!isLBPriceEnabled}
                placeholder={t('placeholders.enterLbPrice')}
              />
              {renderErrorMessage('shippingCompanyPricePerLB')}
            </div>
            )}
            <div className="col-md-4">
              <div className="form-check form-switch d-flex align-items-center">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="shippingInsuranceSwitch"
                  name="hasShippingInssurance"
                  checked={formData.hasShippingInssurance}
                  onChange={handleInputChange}
                  style={{ margin: 0 }}
                />
                <label className="form-check-label mb-0" htmlFor="shippingInsuranceSwitch">
                  {t('fields.hasShippingInsurance')}?
                </label>
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-check form-switch d-flex align-items-center">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="enableDGFeesSwitch"
                  name="enableDGFees"
                  checked={formData.enableDGFees}
                  onChange={handleInputChange}
                  style={{ margin: 0 }}
                />
                <label className="form-check-label mb-0" htmlFor="enableDGFeesSwitch">
                  {t('fields.enableDGFees')}
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Extra Information Section */}
      {(formData.shippingOrderNumber || formData.refundInvoiceNumber || formData.orderId || formData.number || formData.refundType > 0) && (
        <div className="card mb-4">
          <div className="card-header bg-dark text-white">
            <h5 className="mb-0">
              {/* <i className="fas fa-info-circle me-2"></i> */}
              {t('sections.extraInformation')}
            </h5>
          </div>
          <div className="card-body">
            <div className="row">
              {formData.shippingOrderNumber && (
                <div className="col-md-3">
                  <label className="form-label">{t('fields.shippingOrderNumber')}</label>
                  <input
                    type="text"
                    className="form-control"
                    name="shippingOrderNumber"
                    disabled
                    value={formData.shippingOrderNumber || ""}
                  />
                </div>
              )}

              {formData.refundInvoiceNumber && (
                <div className="col-md-3">
                  <label className="form-label">{t('fields.refundInvoiceNumber')}</label>
                  <input
                    type="text"
                    className="form-control"
                    name="refundInvoiceNumber"
                    disabled
                    value={formData.refundInvoiceNumber || ""}
                  />
                </div>
              )}

              {formData.orderId && (
                <div className="col-md-3">
                  <label className="form-label">{t('fields.reorderedId')}</label>
                  <input
                    type="number"
                    className="form-control"
                    name="orderId"
                    disabled
                    value={formData.orderId}
                  />
                </div>
              )}

              {formData.number && (
                <div className="col-md-3">
                  <label className="form-label">{t('fields.orderNumber')}</label>
                  <input
                    type="text"
                    className="form-control"
                    name="orderNumber"
                    disabled
                    value={formData.number || ""}
                  />
                </div>
              )}


            </div>

            {formData.refundType > 0 && (
              <>
                <div className="row mt-3">
                  <div className="col-md-12">
                    <h6 className="text-muted border-bottom pb-2">
                      <i className="fas fa-undo me-2"></i>
                      {t('sections.refundDetails', 'Refund Details')}
                    </h6>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-3">
                    <label className="form-label">{t('fields.refundType')}</label>
                    <input
                      type="text"
                      className="form-control"
                      name="orderNumber"
                      disabled
                      value={formData.refundType == 1 ? t('refundTypes.1') : t('refundTypes.2')}
                    />
                  </div>
                  {formData.refundType == 2 && (
                    <div className="col-md-3">
                      <label className="form-label">{t('fields.refundCode')}</label>
                      <input
                        type="text"
                        className="form-control"
                        name="orderNumber"
                        disabled
                        value={formData.number ? `RFND-${formData.number}` : ''}
                      />
                    </div>
                  )}

                  {formData.refundType == 1 && (
                    <div className="col-md-3">
                      <label className="form-label">{t('fields.refundReceipt')}</label>
                      <input
                        type="text"
                        className="form-control"
                        name="orderNumber"
                        disabled
                        value={formData.refundInvoiceNumber || ""}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="text-center mt-4">
        {formData.orderStatus === 0 && (
          <>
            <div className="mb-3" style={{ maxWidth: "600px", margin: "0 auto" }}>
              <label className="form-label d-block text-start">Reject Note</label>
              <textarea
                className="form-control"
                rows={3}
                value={pendingRejectComment}
                onChange={(e) => setPendingRejectComment(e.target.value)}
                placeholder="Write note before rejecting"
              />
            </div>

            {/* While an action runs, hide all buttons and show a single spinner */}
            <ProcessingButtons processing={updating || virtualCalculating || partialPaymentCalculating}>
              {/* Show different buttons based on payment type. A group pays in part as a
                  whole, so its items are validated like any other. */}
              {!groupOwnsTerms && formData.paymentType == 1 ? (
                // Partial Payment buttons
                <>
                  <button
                    className={`btn btn-info ${isRTL ? 'ms-2' : 'me-2'}`}
                    onClick={handleVirtualPartialCalculation}
                    disabled={virtualCalculationDone}
                  >
                    {t('actions.virtualPartialCalculation')}
                  </button>
                  <button
                    className={`btn btn-success ${isRTL ? 'ms-2' : 'me-2'}`}
                    onClick={handlePartialPaymentValidationCalculation}
                    disabled={!virtualCalculationDone}
                  >
                    {t('actions.partialPaymentValidationCalculation')}
                  </button>
                </>
              ) : (
                // Regular validation button (hidden for partial payment)
                <button className={`btn btn-success ${isRTL ? 'ms-2' : 'me-2'}`} onClick={handleUpdateOrder}>
                  {t('actions.validateOrder')}
                </button>
              )}
              <button
                className="btn btn-danger"
                onClick={() => handleStatusUpdate(2, pendingRejectComment)}
              >
                {t('actions.invalidateOrder')}
              </button>
            </ProcessingButtons>
          </>
        )}

        {formData.orderStatus == 5 &&
          hasPermission("Super Admin Approval Order") && (
            <>
              <button className={`btn btn-success ${isRTL ? 'ms-2' : 'me-2'}`} onClick={handleUpdateOrder}>
                {updating ? t('actions.updating') : t('actions.updateOrder')}
              </button>
            </>
          )}
        {/* While calculating or resetting, hide both buttons and show a single spinner */}
        <ProcessingButtons processing={calculatingOrder || resettingOrder}>
          {formData.orderStatus === 1 && (
            <button
              className={`btn btn-primary ${isRTL ? 'ms-2' : 'me-2'}`}
              type="button"
              onClick={calculateOrder}
            >
              <i className="fa fa-dollar"></i>
              {t('actions.calculateOrder')}
            </button>
          )}
          {formData.orderStatus <= 6 && formData.orderStatus > 0 && (
            <button
              className={`btn btn-primary ${isRTL ? 'ms-2' : 'me-2'}`}
              type="button"
              onClick={resetOrder}
            >
              <i className="fa fa-recycle"></i>
              {t('actions.resetOrder')}
            </button>
          )}
        </ProcessingButtons>

        {formData.orderStatus === 4 && (
          <>
            <p>{t('messages.rejectedShipping')}</p>
          </>
        )}

        {formData.orderStatus === 2 && (
          <>
            <p>{t('messages.rejected')}</p>
          </>
        )}
      </div>
    </div>
  );
};
